import React, { useState, useEffect } from 'react';
import { X, Search, Send, Check } from 'lucide-react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const ShareModal = ({ isOpen, onClose, post }) => {
    const { t } = useLanguage();
    const [connections, setConnections] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [personalNote, setPersonalNote] = useState('');
    const [isBroadcasting, setIsBroadcasting] = useState(false);
    const [broadcastComplete, setBroadcastComplete] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchConnections();
            document.body.style.overflow = 'hidden';
            setSelectedIds([]);
            setPersonalNote('');
            setBroadcastComplete(false);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const fetchConnections = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/connections');
            const friends = data.filter(c => c.status === 'accepted');
            setConnections(friends);
        } catch (err) {
            console.error('Failed to fetch friends:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelection = (userId) => {
        if (broadcastComplete) return;
        setSelectedIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleBroadcast = async () => {
        if (selectedIds.length === 0 || isBroadcasting) return;

        setIsBroadcasting(true);
        try {
            const shareLink = `${window.location.origin}/profile/${post.user_id}?post=${post.id}`;
            const noteSection = personalNote.trim() ? `\n\nNote from sender: "${personalNote.trim()}"` : '';
            const previewText = post.poll_question
                ? `POLL: ${post.poll_question}`
                : post.celebration_type
                    ? `CELEBRATION: ${post.celebration_type}`
                    : post.event_date
                        ? "NETWORK EVENT"
                        : post.content;

            const message = `[SHARED TRANSMISSION]${noteSection}\n\n${(previewText || '').substring(0, 100)}${(previewText || '').length > 100 ? '...' : ''}\n\nView full post: ${shareLink}`;

            // Send all messages in parallel
            await Promise.all(selectedIds.map(receiverId =>
                api.post('/messages', { receiver_id: receiverId, content: message })
            ));

            setBroadcastComplete(true);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err) {
            console.error('Broadcast failed:', err);
            alert('Transmission failure during broadcast. Please try again.');
        } finally {
            setIsBroadcasting(false);
        }
    };

    if (!isOpen) return null;

    const filtered = connections.filter(c =>
        (c.full_name || c.username || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)'
        }}>
            <div className="glass-card animate-scale-up" style={{
                width: '90%', maxWidth: '450px', maxHeight: '85vh',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                borderRadius: '24px', position: 'relative'
            }}>
                {/* Header */}
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3 style={{
                            fontSize: '1.2rem',
                            fontWeight: 900,
                            background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            {broadcastComplete ? t('share_complete_title') : t('share_title')}
                        </h3>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                            {broadcastComplete ? t('share_complete_subtitle') : t('share_subtitle')}
                        </p>
                    </div>
                    <X size={20} color="var(--text-muted)" style={{ cursor: 'pointer' }} onClick={onClose} />
                </div>

                {/* Search & Actions */}
                {!broadcastComplete && (
                    <div style={{ padding: '1rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <Search size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="text"
                                placeholder={t('search_friends_placeholder')}
                                className="cyber-input"
                                style={{ paddingLeft: '2.5rem', fontSize: '0.9rem', width: '100%' }}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        {connections.length > 0 && (
                            <button
                                onClick={() => setSelectedIds(selectedIds.length === connections.length ? [] : connections.map(c => c.id))}
                                style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                                {selectedIds.length === connections.length ? t('clear_selection') : t('select_all')}
                            </button>
                        )}
                    </div>
                )}

                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
                    {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>{t('sync_connections')}</div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                            {searchTerm ? t('no_matching_connections') : t('no_active_connections')}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {filtered.map(friend => {
                                const isSelected = selectedIds.includes(friend.id);
                                return (
                                    <div
                                        key={friend.id}
                                        onClick={() => toggleSelection(friend.id)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '1rem',
                                            padding: '0.75rem', borderRadius: '16px',
                                            background: isSelected ? 'rgba(99, 102, 241, 0.08)' : 'rgba(255,255,255,0.02)',
                                            border: '1px solid',
                                            borderColor: isSelected ? 'var(--accent-primary)' : 'var(--glass-border)',
                                            cursor: broadcastComplete ? 'default' : 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--bg-deep)', overflow: 'hidden', flexShrink: 0 }}>
                                            {friend.avatar_url ? (
                                                <img src={friend.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)' }}>
                                                    {friend.username[0].toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-heading)' }}>{friend.full_name || friend.username}</div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{friend.role_title || t('member_label')}</div>
                                        </div>
                                        <div style={{
                                            width: '20px', height: '20px', borderRadius: '6px',
                                            border: '2px solid',
                                            borderColor: isSelected ? 'var(--accent-primary)' : 'var(--glass-border)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: isSelected ? 'var(--accent-primary)' : 'transparent',
                                            transition: 'all 0.2s ease'
                                        }}>
                                            {isSelected && <Check size={14} color="white" strokeWidth={4} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div style={{ padding: '1.25rem', background: 'var(--bg-card)', borderTop: '1px solid var(--glass-border)' }}>
                    {!broadcastComplete && (
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '0.5rem', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('personal_note_label')}
                            </label>
                            <textarea
                                className="cyber-input"
                                placeholder={t('personal_note_placeholder')}
                                value={personalNote}
                                onChange={(e) => setPersonalNote(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: '80px',
                                    fontSize: '0.85rem',
                                    padding: '0.75rem',
                                    resize: 'none',
                                    background: 'rgba(0,0,0,0.2)'
                                }}
                            />
                        </div>
                    )}
                    {broadcastComplete ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: 'var(--accent-success)', fontWeight: 800 }}>
                            <Check size={20} /> {t('broadcast_dispatched')}
                        </div>
                    ) : (
                        <button
                            disabled={selectedIds.length === 0 || isBroadcasting}
                            onClick={handleBroadcast}
                            className="btn-cyber"
                            style={{
                                width: '100%',
                                padding: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                opacity: selectedIds.length === 0 ? 0.5 : 1
                            }}
                        >
                            {isBroadcasting ? (
                                t('dispatching')
                            ) : (
                                <>
                                    <Send size={18} />
                                    {t('broadcast_to_label')} {selectedIds.length} {selectedIds.length === 1 ? t('colleague_single') : t('colleague_plural')}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
